import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GraduationCap, Eye, Edit, Trash, Users } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Reactable } from "../../components/ui/reactable";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { useTrainingPermissions } from "../../hooks/useTrainingPermissions";
import {
    TRAINING_TASKS,
    TRAINING_MY_TASKS,
    TRAINING_TEAM_TASKS,
} from "../../constants/api";

/* Scope tabs, in the same shape grievance uses for its inbox queues. Status
   stays a server-side scope because the admin list is of TASKS, whose status
   lives on their assignments rather than on the row itself. */
const SCOPE_TABS = [
    { value: "", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "submitted", label: "Submitted" },
    { value: "overdue", label: "Overdue" },
    { value: "completed", label: "Completed" },
];

const PRIORITY_OPTIONS = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
];

const statusStyles = {
    pending: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    overdue: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    // Submitted is deliberately neutral-positive: the employee is done, the
    // wait is on the verifier, so it must not read as a failure state.
    submitted: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
    rejected: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

const statusLabels = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    overdue: "Overdue",
    submitted: "Awaiting Verification",
    rejected: "Sent Back",
};

const priorityStyles = {
    low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    high: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const StatusPill = ({ value }) => (
    <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", statusStyles[value] || statusStyles.pending)}>
        {statusLabels[value] || value}
    </span>
);

const PriorityPill = ({ value }) => (
    <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full capitalize", priorityStyles[value] || priorityStyles.medium)}>
        {value}
    </span>
);

/* Column definitions. `filterable` puts a column in Reactable's funnel
   popover; `filterType: "select"` makes that a dropdown rather than a text
   box. Client-side matching compares the RAW value at `accessor`, not what
   Cell renders, so status columns filter on the stored code. */
const adminColumns = ({ onView, onEdit, onDelete, departmentOptions }) => [
    {
        accessor: "title", Header: "Title", sortable: true, filterable: true,
        Cell: ({ value }) => <span className="font-medium text-foreground">{value || "-"}</span>,
    },
    {
        accessor: "total_assignees", Header: "Assignees", sortable: true,
        Cell: ({ value }) => (
            <Badge variant="secondary"><Users className="h-3 w-3 mr-1" />{value ?? 0}</Badge>
        ),
    },
    {
        accessor: "due_date", Header: "Due Date", sortable: true, filterable: true, filterType: "date",
        Cell: ({ value }) => (value ? dayjs(value).format("DD-MM-YYYY") : "-"),
    },
    {
        accessor: "priority", Header: "Priority", sortable: true, filterable: true,
        filterType: "select", filterOptions: PRIORITY_OPTIONS,
        Cell: ({ value }) => <PriorityPill value={value} />,
    },
    {
        accessor: "department_names", Header: "Department", filterable: true,
        filterType: "select", filterOptions: departmentOptions,
        // A task can span several departments, so the cell shows the first and
        // counts the rest. The accessor holds all of them joined, which is what
        // the filter matches against -- picking "Nursing" keeps any task with a
        // nursing assignee, the same rows the server-side filter used to return.
        Cell: ({ row }) => {
            const names = row.department_list || [];
            if (!names.length) return "-";
            return (
                <span>
                    {names[0]}
                    {names.length > 1 && (
                        <span className="text-muted-foreground"> +{names.length - 1}</span>
                    )}
                </span>
            );
        },
    },
    {
        accessor: "completion_percent", Header: "Completion", sortable: true,
        Cell: ({ row }) => `${row.completed_count}/${row.total_assignees} (${row.completion_percent}%)`,
    },
    {
        accessor: "actions", Header: "Actions",
        Cell: ({ row }) => (
            <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onView(row.id); }}>
                    <Eye className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onEdit(row.id); }}>
                    <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); onDelete(row); }}>
                    <Trash className="h-4 w-4" />
                </Button>
            </div>
        ),
    },
];

const assignmentColumns = ({ showEmployee, onView, departmentOptions }) => [
    {
        accessor: "title", Header: "Title", sortable: true, filterable: true,
        Cell: ({ value }) => <span className="font-medium text-foreground">{value || "-"}</span>,
    },
    ...(showEmployee ? [{
        accessor: "employee_name", Header: "Employee", sortable: true, filterable: true,
    }] : []),
    {
        accessor: "due_date", Header: "Due Date", sortable: true, filterable: true, filterType: "date",
        Cell: ({ value }) => (value ? dayjs(value).format("DD-MM-YYYY") : "-"),
    },
    {
        accessor: "priority", Header: "Priority", sortable: true, filterable: true,
        filterType: "select", filterOptions: PRIORITY_OPTIONS,
        Cell: ({ value }) => <PriorityPill value={value} />,
    },
    {
        accessor: "employee_department", Header: "Department", sortable: true,
        filterable: true, filterType: "select", filterOptions: departmentOptions,
        Cell: ({ value }) => value || "-",
    },
    {
        accessor: "effective_status", Header: "Status", sortable: true, filterable: true,
        filterType: "select",
        filterOptions: Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
        Cell: ({ value }) => <StatusPill value={value} />,
    },
    {
        accessor: "action", Header: "Action",
        Cell: ({ row }) => (
            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onView(row.id); }}>
                <Eye className="h-4 w-4" />
            </Button>
        ),
    },
];

const TrainingList = ({ defaultFilter = null, mode = "auto" }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fetch, remove } = useApiMethods();

    const { canAssignTraining } = useTrainingPermissions();

    const isTeamView = mode === "team" || location.pathname.endsWith("/team");
    const isAdminView = !isTeamView && canAssignTraining;

    const [tasks, setTasks] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(defaultFilter || "");
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Routes /training and /training/overdue resolve to the same component instance,
    // so the defaultFilter prop changes but state seeded from it at mount doesn't.
    // Re-sync whenever the route (and therefore defaultFilter) changes — this cascades
    // through loadData's deps and re-fires the GET with the right status param.
    useEffect(() => {
        setStatusFilter(defaultFilter || "");
    }, [defaultFilter, location.pathname]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            if (isAdminView) {
                const filters = {};
                if (statusFilter) filters.status = statusFilter;
                const res = await fetch(TRAINING_TASKS, filters);
                setTasks(res?.data || []);
            } else {
                const url = isTeamView ? TRAINING_TEAM_TASKS : TRAINING_MY_TASKS;
                const filters = {};
                if (statusFilter) filters.status = statusFilter;
                const res = await fetch(url, filters);
                setAssignments(res?.data || []);
            }
        } catch (err) {
            ToastMsg("error", "Failed to load trainings");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdminView, isTeamView, statusFilter]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await remove(`${TRAINING_TASKS}?id=${deleteTarget.id}`);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Training removed");
                setDeleteTarget(null);
                loadData();
            } else {
                ToastMsg("error", res?.message || "Failed to remove training");
            }
        } catch (_) {
            ToastMsg("error", "Failed to remove training");
        }
    };

    // Reactable matches on the raw value at `accessor`, so a task's departments
    // are flattened to one string per row before it sees them.
    const adminRows = useMemo(() => tasks.map((t) => {
        const names = [...new Set(
            (t.assignments || [])
                .map((a) => a.employee_department)
                .filter(Boolean),
        )];
        return { ...t, department_list: names, department_names: names.join(", ") };
    }), [tasks]);

    // Built from what is actually on screen rather than the full department
    // list, so the dropdown never offers a choice that matches nothing.
    const departmentOptions = useMemo(() => {
        const source = isAdminView
            ? adminRows.flatMap((r) => r.department_list)
            : assignments.map((a) => a.employee_department);
        return [...new Set(source.filter(Boolean))]
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ value: name, label: name }));
    }, [isAdminView, adminRows, assignments]);

    const headerTitle = isAdminView
        ? "All Trainings"
        : isTeamView
            ? "Team Trainings"
            : "My Trainings";
    const headerSubtitle = isAdminView
        ? "Assign and track training tasks across departments"
        : isTeamView
            ? "Trainings assigned to your team"
            : "Trainings assigned to you";

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={GraduationCap}
                title={headerTitle}
                subtitle={headerSubtitle}
                totalRecords={isAdminView ? tasks.length : assignments.length}
                buttonEnable={isAdminView}
                setOpenDialog={() => navigate("/training/add")}
                buttonName={"Assign Training"}
            />

            <div className="mb-2 flex flex-wrap items-center gap-3">
                <SegmentSwitch options={SCOPE_TABS} value={statusFilter} onChange={setStatusFilter} />
            </div>

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading...</div>
            ) : (
                <Reactable
                    key={`${isAdminView ? "tasks" : "assignments"}-${statusFilter}`}
                    pagename={`training-${isAdminView ? "all" : isTeamView ? "team" : "mine"}`}
                    data={isAdminView ? adminRows : assignments}
                    setData={isAdminView ? setTasks : setAssignments}
                    columns={isAdminView
                        ? adminColumns({
                            departmentOptions,
                            onView: (id) => navigate(`/training/task/${id}`),
                            onEdit: (id) => navigate(`/training/edit/${id}`),
                            onDelete: (t) => setDeleteTarget(t),
                        })
                        : assignmentColumns({
                            departmentOptions,
                            showEmployee: isTeamView,
                            onView: (id) => navigate(`/training/view/${id}`),
                        })}
                    isCSVRequired
                    isPDFRequired
                    url={isAdminView
                        ? TRAINING_TASKS
                        : isTeamView ? TRAINING_TEAM_TASKS : TRAINING_MY_TASKS}
                    printFilter={statusFilter ? { status: statusFilter } : {}}
                    rowSize="sm"
                    tableHeight="calc(100vh - 330px)"
                />
            )}

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remove Training"
                description={`Are you sure you want to remove "${deleteTarget?.title}"? This will deactivate it for all assignees.`}
                confirmText="Remove"
            />
        </ContainerCard>
    );
};

export default TrainingList;
